import { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { initializeIcons } from 'office-ui-fabric-react/lib/Icons';
import i18n, { TFunction } from 'i18next';
import LocalStorageBackend from 'i18next-localstorage-backend';
import Backend from 'i18next-chained-backend';
import HttpBackend from 'i18next-xhr-backend';
import { initReactI18next } from 'react-i18next';
import { GlobalConfiguration, getUrlParameter, Container, GeException, DefaultLogger, LogMessageType } from './ts/infrastructure';
import {
    OfficeBootstrapFailure,
    cancelOfficeInitWatchdog,
    officeBootstrapFailureMessage,
    officeCloud,
    officeJsLoaded,
    officeJsSrc,
    officeMailbox,
    renderOfficeBootstrapFailure,
    startOfficeInitWatchdog,
} from './ts/infrastructure/officeBootstrap';
import OnSendHook, { SendSecureResult } from './ts/Ge/onSendHook';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import './styles.scss';
// import 'office-ui-fabric-react/dist/css/fabric.min.css';
import { GeApi } from './ts/Ge';
import { AuditEvents, getApmSafeState, getOutlookPlatform, transformTenant } from './ts/infrastructure/utils';
import { APM_ENV } from './ts/infrastructure/constants';
import { ApmBase, init as initApm, Span, Transaction } from '@elastic/apm-rum';
import { PlatformData } from './ts/Ge/types';
import App from './components/App';
import NonOfficeApp from './components/NonOfficeApp';

const globalCfg = new GlobalConfiguration();
const isPortal = getUrlParameter('portal');
const modalTraceId = getUrlParameter('trace_id');
// const serverUrl = getUrlParameter('server_url');

let apm: ApmBase | null = null;
let span: Span;
let transaction: Transaction;
type ApmData = {
    result: string;
    resultCode: string;
    error: string;
    originalError?: string;
    tenant?: string;
}
const tenant = getUrlParameter('deployment');

// Versions before AB#15755 cached the CWP token in localStorage. Drop that copy as soon as any
// context loads, so the upgrade clears it even for a user who never sends again.
localStorage.removeItem('cwpToken');

// init for modal only, not preload.
// We do this to record validation page-load linked to the email compose transaction.
if (isPortal && modalTraceId) {
    initializeApm();
    const transaction = apm.getCurrentTransaction();
    if (transaction && modalTraceId !== 'unknown') {
        (transaction as any).traceId = modalTraceId; // Internal API to override traceId, not great \o/
    }
}

async function initI18n(): Promise<TFunction> {
    return await i18n
        .use(Backend)
        .use(initReactI18next)
        .init({
            fallbackLng: 'en',
            preload: ['en'],
            lng: 'en',
            ns: ['default', tenant],
            fallbackNS: 'default',
            defaultNS: tenant,
            interpolation: {
                escapeValue: false
            },
            backend: {
                backends: [
                    LocalStorageBackend,  // primary
                    HttpBackend           // fallback
                ],
                backendOptions: [{
                    // bump translation version to bust http cache on Outlook..
                    prefix: `i18n_ESG__${__VERSION__}_`,
                    expirationTime: 12 * 60 * 60 * 1000, // 12h.
                },
                {
                    loadPath: `/translations/${__VERSION__}/{{ns}}/{{lng}}.json`,
                }]
            },
            react: {
                useSuspense: true
            }
        });
}


const render = (Component: any) => {

    const loadingSpinner = (
        <div id="loading_spinner">
            <Spinner size={SpinnerSize.large} style={{ paddingTop: 150 }} />
        </div>
    );

    ReactDOM.render(
        <Suspense fallback={loadingSpinner}>
            <Component container={Container.NATIVE_OUTLOOK} apm={apm} />
        </Suspense>
        , document.getElementById('container')
    );
};

async function init() {
    const action = getUrlParameter('action');
    if (action && action === 'send') {
        return;
    }

    initI18n();
    initializeIcons(
        'https://res.cdn.office.net/files/fabric-cdn-prod_20240129.001/assets/icons/',
    );

    render(App);
}

/* ---------------------------------------------------------------------------------------- */
/* office.js bootstrap failures */
/* ---------------------------------------------------------------------------------------- */
// Report the failure, then show it. Reporting first means we still get telemetry even if
// rendering throws, and it is the only signal we get for the 'blank task pane' failure -
// see the header comment in officeBootstrap.ts for why it is otherwise invisible.
function onOfficeBootstrapFailure(failure: OfficeBootstrapFailure): void {
    const message = officeBootstrapFailureMessage(failure);

    try {
        initializeApm();
        // officeCloud() reads the cloud off the office.js tag. Do not replace it with a
        // build-time constant: the shipped bundle is built once with no cloud baked in and
        // nginx rewrites the tag per web app at container start, so a compiled value would
        // label every gov page `commercial` (AB#14694).
        // Read once. Both fields below must agree — a context and a label that disagreed
        // about the cloud would be worse than either being absent.
        const cloud = officeCloud();
        apm?.setCustomContext({
            "office-js-src": officeJsSrc(),
            "office-cloud": cloud,
            resultCode: failure,
        });
        apm?.addLabels({
            resultCode: failure,
            "office-cloud": cloud,
        });
        apm?.captureError(new Error(message));
    } catch {
        // APM itself is served from a public host, so on a locked-down network it can be blocked
        // by the same policy that blocked office.js. Never let that hide the on-screen message.
    }

    try {
        // Written to the same localStorage-backed log LogDialog reads at /debug-dialog
        // (new DefaultLogger() always points at the 'addin-logger' key, regardless of which
        // call site created the instance - see App.tsx and onSendHook.ts for the same
        // pattern). APM needs our network to be unblocked to receive anything; this doesn't -
        // it's the one channel a customer can gather for us even when the office.js CDN itself
        // is what got blocked, by asking them to open the debug dialog and copy its contents.
        new DefaultLogger().logMessageWithType(LogMessageType.error, message);
    } catch {
        // localStorage can throw (private browsing, storage quota, browser policy) - never let
        // that hide the on-screen message either.
    }

    console.error(message);
    renderOfficeBootstrapFailure(failure);
}

/* ---------------------------------------------------------------------------------------- */
/* Entry points */
/* ---------------------------------------------------------------------------------------- */
// Every reference to `Office` below is reachable only past this guard. `Office` is undeclared
// when office.js failed to load, which makes even `typeof Office?.context?.mailbox` throw, so
// this has to be checked before anything else touches it.
if (!officeJsLoaded()) {
    onOfficeBootstrapFailure('office-js-not-loaded');
} else {
    const isSendHandler = getUrlParameter('action') === 'send';

    // Office.context.mailbox is not defined in the validation modal (isPortal) or the on-send
    // function file (isSendHandler) either - both are legitimate Office-driven surfaces that
    // just don't happen to have a synchronous mailbox reference at this point. The real taskpane
    // is different: this exact "mailbox undefined -> render NonOfficeApp" check predates this PR
    // (it was already the app's mechanism for detecting a real Outlook host before any of this
    // bootstrap work existed), so a real taskpane reliably HAS a synchronous mailbox reference
    // here - if it didn't, that pre-existing check would already have been broken in production.
    // noOfficeHost is true only when NONE of the three apply: no mailbox, not the modal, not
    // on-send - i.e. this page was opened directly in a plain browser tab rather than by Outlook
    // at all (this is exactly what `npm run dev` looks like when opened without sideloading).
    const noOfficeHost = typeof officeMailbox() === "undefined" && !isPortal && !isSendHandler;

    if (noOfficeHost) {
        // Render app out of Office context.
        render(NonOfficeApp);
    } else {
        // The watchdog covers the second failure shape: office.js is present, but the host
        // never calls back, so init() never runs and the pane stays blank. Gated on
        // !noOfficeHost: when there is no Office host at all, Office.initialize was never
        // going to be called in the first place, so watching for it would eventually fire a
        // false "could not start" diagnostic over the NonOfficeApp screen that rendered
        // correctly above - exactly what happened before this guard existed.
        //
        // Armed for the on-send function file too, not excluded: Microsoft's on-send docs
        // describe event handlers as needing to be "defined by the time Office.initialize or
        // Office.onReady() is called", and their own sample registers Office.context.mailbox
        // inside Office.initialize itself - so a stalled handshake there is a real, silent
        // failure of the on-send validation, not a diagnostic-free runtime. It matters more
        // here than in the taskpane: the newest manifests drop the taskpane entirely, so
        // on-send is the only surface some deployments run at all. Nothing renders visibly
        // either way - init() itself no-ops for action=send - so arming this only adds an
        // APM signal, never a user-visible false positive.
        startOfficeInitWatchdog(() => onOfficeBootstrapFailure('office-initialize-never-fired'));
    }

    Office.initialize = () => {
        cancelOfficeInitWatchdog();
        // /* Render application after Office initializes */
        init();
    };
}

function initializeApm() {
    apm = initApm({
        serviceName: 'esg-outlook',
        serverUrl: "https://apm.e-share.io",
        // needed for sourcemaps
        serviceVersion: globalCfg.getVersion(),
        transactionSampleRate: 1.0,
        pageLoadTransactionName: isPortal ? 'Route: Validation modal' : 'Route: Send email',
        flushInterval: 0,
        // distributedTracingOrigins: ["https://graph.microsoft.com", "https://ppe.eshareitar.com", serverUrl],
        // propagateTracestate: true,
        distributedTracing: false,
        // logLevel: __DEV__ ? 'debug' : 'warn',
        environment: __DEV__ ? 'development' : APM_ENV,
        // apmRequest: ({ xhr }) => {
        //     xhr.setRequestHeader('cf-client-id', '10c4e87c11efb7ae8f95503d');
        //     return true;
        // },
    });
    const mailbox = officeMailbox();
    if (mailbox) {
        apm.setUserContext({
            email: mailbox.userProfile.emailAddress
        })
    } else {
        // A corrupted or malformed GeApiState value must not throw past this point: this
        // function is now also called from onOfficeBootstrapFailure, whose single try/catch
        // would otherwise skip setCustomContext/addLabels/captureError entirely if this throws.
        // (A MISSING key does not need this guard - localStorage.getItem returns null, and
        // JSON.parse(null) parses the string "null" successfully; only a malformed non-null
        // value throws.)
        let apiState;
        try {
            apiState = JSON.parse(localStorage.getItem("GeApiState"))
        } catch {
            // Fall through with apiState left undefined - no user context is set, same as the
            // pre-existing "no GeApiState at all" case below.
        }
        if (apiState) {
            apm.setUserContext({
                email: apiState.decodedTokenPayload?.smtp || apiState.decodedTokenPayload?.upn || apiState.decodedTokenPayload?.preferred_username
            })
        }
    }
}
/* ---------------------------------------------------------------------------------------- */
/* Entry point for onSend action */
/* ---------------------------------------------------------------------------------------- */
async function onSendHook(event: Office.AddinCommands.Event): Promise<void> {
    // event.completed() is what actually releases Outlook's synchronous ItemSend handler. Every path
    // in this function already calls it, but several of those paths run more unguarded localStorage
    // calls first (audit lookups, context state, cleanup) - if the host's storage API is unavailable
    // and throws, the call that was supposed to complete the event never runs, and Outlook hangs
    // forever with no signal anywhere (no "Send email" transaction, no captured error). completeEvent
    // is idempotent and never itself throws, so it is safe to call from the guaranteed last line of
    // the outer finally below as a true backstop, in addition to every existing call site.
    let eventCompleted = false;
    const completeEvent = (allowEvent: boolean) => {
        if (eventCompleted || !event) return;
        eventCompleted = true;
        try {
            event.completed({ allowEvent });
        } catch {
            // event.completed itself must never be allowed to throw and mask the real failure.
        }
    };

    let isAppointment = false;
    let sendEmailResponse: SendSecureResult = {
        allowEvent: true
    };
    let apiInstance: GeApi | undefined;
    let platformInfo = {} as PlatformData;

    try {
        initializeApm();
        const platform = getOutlookPlatform();
        isAppointment = Office.context.mailbox.item.itemType === Office.MailboxEnums.ItemType.Appointment;

        if (Office.context.mailbox && Office.context.mailbox.diagnostics) {
            const outlookHostInfo = Office.context.mailbox.diagnostics;
            // Store platform info to use in audits and errors.
            platformInfo = {
                platform,
                version: outlookHostInfo.hostVersion,
            };
            localStorage.setItem("platformInfo", JSON.stringify(platformInfo));
        } else {
            // If diagnostics aren't available, try to load from localStorage
            platformInfo = JSON.parse(localStorage.getItem("platformInfo") || "{}");
        }

        if (!isAppointment) {
            const sendHookHandler = new OnSendHook();
            const t = await initI18n();
            // Retrieve the page load transaction's traceId
            const pageTransaction = apm.getCurrentTransaction();
            const pageloadTraceId = (pageTransaction as any)?.traceId || 'unknown';

            transaction = apm?.startTransaction('Send email', 'user-action', { managed: true });
            // link our custom transaction with page-load transaction
            if (transaction && pageloadTraceId !== 'unknown') {
                (transaction as any).traceId = pageloadTraceId; // Internal API to override traceId
            }

            span = transaction?.startSpan('Processing email', 'add-in', { blocking: true });

            sendEmailResponse = await sendHookHandler.onSend(t, Container.NATIVE_OUTLOOK, apm);
        }

        apiInstance = sendEmailResponse?.apiInstance;

        if (event) {
            let result = "Event completed without blocking errors";
            let resultCode = "sent-no-error";

            // Check transaction labels for errors that did not reject the operation
            //  - like getItemClass() or getSensitivityLabel() - to properly update result.
            if ((transaction as any)?.context?.tags?.error) {
                result = "Event completed with non blocking error";
                resultCode = "sent-non-blocking-error";
            };

            try {
                const isGeApiInstance = apiInstance instanceof GeApi;
                if (!isAppointment && isGeApiInstance) {
                    const auditsAttempted = JSON.parse(localStorage.getItem("auditEvents"))?.join(", ");
                    if (sendEmailResponse.allowEvent) {
                        if (apiInstance.recipientsToBeRemoved.length > 0 || apiInstance.recipientsOverriddenByUser.length > 0) {
                            await apiInstance.sendAuditEvent(AuditEvents.USER_DECISION);
                        }
                        await apiInstance.sendAuditEvent(AuditEvents.EMAIL_SENT);
                    } else {
                        if ( auditsAttempted && auditsAttempted.includes(AuditEvents.CANCEL)) {
                            resultCode = "send-cancelled";
                            result = "Event cancelled by the user";
                        }
                        else {
                            // Event actually not allowed!
                            resultCode = "send-not-allowed";
                            result = "Event completed without blocking errors but not allowed (no valid recipients left after validation)";
                        }

                    }

                    apm.setCustomContext({
                        "audit-events-attempted": auditsAttempted,
                    });
                } else {
                    resultCode = "sent-appoinment-not-allowed";
                    result = "Event completed appointment not allowed apiinstance";

                    apm.setCustomContext({
                        isAppointment,
                        sendEmailResponse,
                        isGeApiInstance
                    });
                }

                localStorage.removeItem(`catalog_${apiInstance?.getEmailId()}`);
            } catch (e) {
                resultCode = "sent-non-blocking-error";
                result = "Event completed with non blocking error on localstorage clear or auditing";
                apm.setCustomContext({
                    error: e?.message,
                });
                apm.addLabels({
                    error: e?.message,  
                });
                apm.captureError(e);
            }

            const apmData = {
                result,
                resultCode,
                "audit-events-attempted": JSON.parse(localStorage.getItem("auditEvents"))?.join(", "),
                tenant: transformTenant(tenant),
            };

            apm.setCustomContext({
                ...apmData,
                state: getApmSafeState(),
                platform: platformInfo,
            });

            apm.addLabels({
                ...apmData,
                platform: platformInfo?.platform,
                "platform-version": platformInfo?.version,
            });

            // End telemetry before signaling Office the send can proceed: once completeEvent()
            // returns, the host may tear down this webview at any time, and there's no
            // unload/visibilitychange event here for the APM transport to flush on. Ending here
            // gives it the best chance to actually deliver this transaction (AB#14110).
            span?.end();
            transaction?.end();

            completeEvent(sendEmailResponse?.allowEvent);
        }
    } catch (e) {
        const originalError = e?.originalError;
        const originalErrorMessage = originalError instanceof Error
            ? originalError?.message
            : originalError instanceof GeException
                ? originalError.getErrorId() + originalError.getErrorLog()
                : JSON.stringify(e);

        const apmData: ApmData = {
            result: "email sent with no blocking error",
            resultCode: "sent-non-blocking-error",
            error: originalErrorMessage,
            tenant: transformTenant(tenant),
        }

        try {
            // may error on sendAudit for some reason
            if (!isAppointment && e?.apiInstance instanceof GeApi) {
                if (e.apiInstance?.cwpToken?.token) {
                    if (e.apiInstance.recipientsToBeRemoved.length > 0 || e.apiInstance.recipientsOverriddenByUser.length > 0) {
                        await e.apiInstance.sendAuditEvent(AuditEvents.USER_DECISION);
                    }
                    await e.apiInstance.sendAuditEvent(AuditEvents.EMAIL_SENT);
                }
                // In apm the audits audit-events-attempted context contain this information.
            }
        } catch (auditError) {
            apmData.result = "Event completed with non blocking error on auditing";
            apmData.resultCode = "sent-non-blocking-error";
            apmData.error = auditError?.message;
            apmData.originalError = originalErrorMessage || auditError?.message;

            apm.setCustomContext({
                ...apmData,
                state: getApmSafeState(),
                platform: platformInfo,
                "audit-events-attempted": JSON.parse(localStorage.getItem("auditEvents"))?.join(", "),
            });
            apm.addLabels({
                ...apmData,
                platform: platformInfo?.platform,
                "platform-version": platformInfo?.version,
            });

            apm.captureError(e);
            apm.captureError(auditError);
            span?.end();
            transaction?.end();

            completeEvent(true);
        }
        
        const apmContext = {
            ...apmData,
            platform: platformInfo,
            "audit-events-attempted": JSON.parse(localStorage.getItem("auditEvents"))?.join(", "),
        }
        apm.setCustomContext({
            ...apmContext,
            state: getApmSafeState(),
        });
        apm.addLabels({
            ...apmContext,
            platform: platformInfo?.platform,
            "platform-version": platformInfo?.version,
        });

        localStorage.removeItem(`catalog_${e?.apiInstance?.getEmailId()}`);

        apm.captureError(apmData.error || e);

        span?.end();
        transaction?.end();

        completeEvent(true);
    } finally {
        // Guaranteed backstop: runs first, before any cleanup below that could itself throw (e.g. if
        // localStorage is what's broken in this session) and prevent this finally block from finishing.
        // A no-op if one of the calls above already completed the event.
        completeEvent(true);

        try {
            localStorage.removeItem("GeApiState");
            localStorage.removeItem("auditEvents");
            localStorage.removeItem("platformInfo");
        } catch {
            // best-effort cleanup only - the event is already completed above regardless.
        }

        try {
            span?.end();
            transaction?.end();
        } catch (telemetryError) {
            // Second attempt (the catch branches above already tried this once) - if it still
            // throws here, the event is already completed above regardless. Capture rather than
            // swallow so a failure this unlikely doesn't go completely unseen.
            apm.captureError(telemetryError);
        }
    }
}

function onNewMessage(event: Office.AddinCommands.Event) {
    // Complete the event, this fires only to preload the add-in assets onCompose.
    event.completed();
}

// Nasty hack to expose this entry point to Outlook JS API.
(window as any).onSendHook = onSendHook;
(window as any).onNewMessage = onNewMessage;